import { cloneElement, ReactElement, useState } from 'react'
import { noop } from 'lodash'

import {
  ClickAwayListener,
  Popper,
  PopperPlacementType,
  styled,
  SxProps,
  Theme
} from '@mui/material'
import Box from '@mui/material/Box'
import Fade from '@mui/material/Fade'
import Paper from '@mui/material/Paper'

type StyledPopperProps = {
  distance: number
}

const StyledPopper = styled(Popper)<StyledPopperProps>(({ theme, distance }) => ({
  zIndex: theme.zIndex.tooltip,
  '&[data-popper-placement*="left"]': {
    '& .MuiPopper-arrow': {
      right: distance,
      color: theme.palette.background.paper,
      '&::before': {
        content: '""',
        margin: 'auto -0.22rem auto auto',
        display: 'block',
        width: '8px',
        height: '8px',
        boxShadow:
          '0px 2px 0px -1px rgb(0 0 0 / 20%), 1px 1px 0px 0px rgb(0 0 0 / 14%), 0px 1px 0px 0px rgb(0 0 0 / 12%)',
        backgroundColor: 'currentColor',
        transform: 'rotate(-45deg)'
      }
    }
  },
  '&[data-popper-placement*="right"]': {
    '& .MuiPopper-arrow': {
      left: distance * 8 - 4,
      color: theme.palette.background.paper,
      '&::before': {
        content: '""',
        margin: 'auto -0.22rem auto auto',
        display: 'block',
        width: '8px',
        height: '8px',
        boxShadow:
          '0px 2px 0px -1px rgb(0 0 0 / 20%), 1px 1px 0px 0px rgb(0 0 0 / 14%), 0px 1px 0px 0px rgb(0 0 0 / 12%)',
        backgroundColor: 'currentColor',
        transform: 'rotate(-225deg)'
      }
    }
  }
}))

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: ReactElement<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: ReactElement<any>
  open: boolean
  onClose?: () => void
  arrow?: boolean
  placement?: PopperPlacementType
  paperSx?: SxProps<Theme>
  distance?: number
}

export const richTooltipTestId = 'rich-tooltip-id'

export const RichTooltip = ({
  placement = 'top',
  arrow = true,
  open,
  onClose = noop,
  content,
  children,
  paperSx,
  distance = 0
}: Props) => {
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null)
  const [childNode, setChildNode] = useState<HTMLElement | null>(null)

  return (
    <div data-testid={richTooltipTestId}>
      {cloneElement(children, { ...children.props, ref: setChildNode })}
      <StyledPopper
        distance={distance}
        open={open}
        anchorEl={childNode}
        placement={placement}
        transition
        disablePortal={false}
        modifiers={[
          {
            name: 'flip',
            enabled: true,
            options: {
              altBoundary: true,
              rootBoundary: 'document',
              padding: 8
            }
          },
          {
            name: 'preventOverflow',
            enabled: false,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'document',
              padding: 8
            }
          },
          {
            name: 'arrow',
            enabled: true,
            options: {
              element: arrowRef
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper sx={{ ...paperSx, marginLeft: distance, marginRight: distance }} elevation={3}>
              <ClickAwayListener onClickAway={onClose}>
                <Paper
                  sx={{
                    backgroundColor: ({ palette }) => palette.background.paper,
                    padding: 0,
                    ...paperSx
                  }}
                >
                  {arrow && <Box data-popper-arrow className="MuiPopper-arrow" ref={setArrowRef} />}
                  <Box sx={{ padding: 1 }}>
                    {cloneElement(content, {
                      ...content.props
                    })}
                  </Box>
                </Paper>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </StyledPopper>
    </div>
  )
}
